/**
 * API Client with JWT Authentication
 *
 * Handles:
 * - JWT token storage and management
 * - Auto-includes Authorization header
 * - 401 redirect to login
 * - File uploads
 */

import { logger } from './logger'
import { API_CONFIG } from './config'

/**
 * Custom error class that preserves the full API error response body.
 * Allows callers to inspect structured error fields (e.g. requires_company_name).
 */
export class ApiError extends Error {
  status: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(message: string, status: number, data: Record<string, any>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

const API_BASE_URL = API_CONFIG.BASE_URL
const TOKEN_KEY = API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN
const REFRESH_TOKEN_KEY = API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN
const TOKEN_EXPIRY_KEY = API_CONFIG.STORAGE_KEYS.TOKEN_EXPIRY

class ApiClient {
  private accessToken: string | null = null
  // @ts-expect-error - Stored for future token refresh feature
  private _refreshToken: string | null = null
  private tokenExpiresAt: number | null = null

  constructor() {
    this.loadTokens()
  }

  /**
   * Load tokens from localStorage
   */
  private loadTokens() {
    if (typeof window === 'undefined') return

    this.accessToken = localStorage.getItem(TOKEN_KEY)
    this._refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY)
    this.tokenExpiresAt = expiryStr ? parseInt(expiryStr, 10) : null

    // Clear if expired
    if (this.tokenExpiresAt && this.tokenExpiresAt < Date.now()) {
      this.clearTokens()
    }
  }

  /**
   * Store JWT tokens
   */
  setTokens(accessToken: string, refreshToken: string, expiresAt: number) {
    logger.debug('API Client: setTokens called', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      expiresAt,
      expiresAtDate: new Date(expiresAt).toISOString(),
      now: Date.now(),
      nowDate: new Date().toISOString(),
      isExpired: expiresAt < Date.now()
    })

    this.accessToken = accessToken
    this._refreshToken = refreshToken
    this.tokenExpiresAt = expiresAt

    if (typeof window !== 'undefined') {
      localStorage.setItem(TOKEN_KEY, accessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString())
    }

    logger.debug('API Client: Tokens stored, isAuthenticated =', this.isAuthenticated())
  }

  /**
   * Clear stored tokens (logout)
   */
  clearTokens() {
    this.accessToken = null
    this._refreshToken = null
    this.tokenExpiresAt = null

    if (typeof window !== 'undefined') {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(TOKEN_EXPIRY_KEY)
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const hasToken = !!this.accessToken
    const hasExpiry = !!this.tokenExpiresAt
    const notExpired = this.tokenExpiresAt ? this.tokenExpiresAt > Date.now() : false
    const result = hasToken && hasExpiry && notExpired

    logger.debug('API Client: isAuthenticated check', {
      hasToken,
      hasExpiry,
      tokenExpiresAt: this.tokenExpiresAt,
      now: Date.now(),
      notExpired,
      result
    })

    return result
  }

  /**
   * Get authorization headers
   */
  private getHeaders(includeContentType = true): HeadersInit {
    const headers: HeadersInit = {}

    if (includeContentType) {
      headers['Content-Type'] = 'application/json'
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    return headers
  }

  /**
   * Handle response and errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      // Unauthorized - clear tokens and redirect to login
      this.clearTokens()
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
      throw new Error('Unauthorized')
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }))
      const message = errorData.detail || errorData.message || 'Request failed'
      throw new ApiError(message, response.status, errorData)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }

  /**
   * Generic request method
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers = this.getHeaders(!options.body || options.body instanceof FormData === false)

    // Don't override Content-Type for FormData
    if (options.body instanceof FormData) {
      delete (headers as Record<string, string>)['Content-Type']
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {}),
      },
    })

    return this.handleResponse<T>(response)
  }

  /**
   * GET request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T>(endpoint: string, options?: { params?: Record<string, any> }): Promise<T> {
    let url = endpoint

    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    return this.request<T>(url, { method: 'GET' })
  }

  /**
   * POST request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T>(endpoint: string, data?: any, options?: { headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      headers: options?.headers,
    })
  }

  /**
   * PUT request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * PATCH request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  /**
   * DELETE request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T>(endpoint: string, options?: { params?: Record<string, any>; data?: any }): Promise<T> {
    let url = endpoint

    // Support query params for DELETE (same as GET)
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    return this.request<T>(url, {
      method: 'DELETE',
      body: options?.data ? JSON.stringify(options.data) : undefined,
    })
  }

  /**
   * Upload file (FormData)
   */
  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.post<T>(endpoint, formData)
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
